import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Box, Divider,
} from '@mui/material';
import { Add, Payments } from '@mui/icons-material';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { sitesApi } from '../../api/sites.api';
import { usersApi } from '../../api/users.api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcPaymentApi, type EpcPayment } from '../../api/epcBilling.api';
import { epcWorkOrderApi } from '../../api/epcWorkOrder.api';
import { epcPoApi } from '../../api/epcProcurement.api';
import { FileUploadField } from '../../components/FileUploadField';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const PAYMENT_MODES = ['NEFT', 'RTGS', 'UPI', 'Cheque', 'Cash'];
const fmtTat = (ms: number): string => {
  if (ms < 0) return '—';
  const m = Math.floor(ms / 60000), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return [d && `${d}d`, h && `${h}h`, (mm || (!d && !h)) && `${mm}m`].filter(Boolean).join(' ');
};

function DetailBlock({ title, lines }: { title: string; lines: Array<string | null | undefined | false> }) {
  const items = lines.filter(Boolean) as string[];
  return (
    <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      {items.length ? items.map((l, i) => <Typography key={i} variant="caption" display="block">{l}</Typography>) : <Typography variant="caption" color="text.secondary">—</Typography>}
    </Box>
  );
}

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'paid' ? 'success' : s === 'approved' ? 'info' : s === 'submitted' ? 'warning' : s === 'rejected' ? 'error' : 'default';

export function EpcPaymentsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [f, setF] = useState({ clientId: '', projectId: '', siteId: '', requestedBy: '', status: '', from: '', to: '' });
  const setFilter = (k: keyof typeof f, v: string) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 500, status: 'active' }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 1000 }) });
  const sites = useQuery({ queryKey: ['sites', 'all'], queryFn: () => sitesApi.list({ limit: 1000 }) });
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => usersApi.list({ limit: 500 }) });
  const clientProjects = (projects.data?.data ?? []).filter((p) => !f.clientId || p.clientId === Number(f.clientId));
  const projectSites = (sites.data?.data ?? []).filter((s) => !f.projectId || s.projectId === Number(f.projectId));

  const params = {
    page, limit,
    clientId: f.clientId || undefined, projectId: f.projectId || undefined, siteId: f.siteId || undefined,
    requestedBy: f.requestedBy || undefined, status: f.status || undefined, from: f.from || undefined, to: f.to || undefined,
  };
  const { data, isLoading } = useQuery({ queryKey: ['epc-payments', params], queryFn: () => epcPaymentApi.list(params) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-payments'] });

  const columns: Column<EpcPayment>[] = [
    { key: 'paymentCode', label: 'Request', render: (r) => r.paymentCode },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'projectName', label: 'Project', render: (r) => r.projectName ? `${r.projectName}${r.projectCode ? ` (${r.projectCode})` : ''}` : `#${r.projectId}` },
    { key: 'payeeName', label: 'Payee', render: (r) => <span>{r.payeeName ?? '—'}<br /><small style={{ color: '#888' }}>{r.workOrderId ? `WO #${r.workOrderId}` : r.purchaseOrderId ? `LPO #${r.purchaseOrderId}` : r.payeeType}</small></span> },
    { key: 'requestedByName', label: 'Requested By', render: (r) => r.requestedByName ?? '—' },
    { key: 'createdAt', label: 'Date', render: (r) => new Date(r.createdAt).toLocaleDateString('en-IN') },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => inr(r.amount) },
    { key: 'status', label: 'Stage', render: (r) => (r.status === 'submitted' ? <Chip size="small" label={r.stageLabel ?? 'In approval'} color="warning" /> : <Chip size="small" label={r.status} color={statusColor(r.status)} />) },
  ];

  return (
    <>
      <PageHeader title="EPC Payment Requests" subtitle="Raise a payment request against a Work Order / Local Purchase, then it moves through the approval chain."
        action={hasPermission('epc_payment.create') && (
          <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setCreateOpen(true); }}>New Payment Request</Button>
        )} />
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Client" value={f.clientId} sx={{ minWidth: 180 }} onChange={(e) => { setF((s) => ({ ...s, clientId: e.target.value, projectId: '', siteId: '' })); setPage(1); }}>
          <MenuItem value="">All</MenuItem>
          {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.clientName}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Project" value={f.projectId} sx={{ minWidth: 200 }} onChange={(e) => { setF((s) => ({ ...s, projectId: e.target.value, siteId: '' })); setPage(1); }}>
          <MenuItem value="">All</MenuItem>
          {clientProjects.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.projectCode} — {p.projectName}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Site" value={f.siteId} sx={{ minWidth: 180 }} onChange={(e) => setFilter('siteId', e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {projectSites.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.siteName} ({s.siteCode})</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Requested By" value={f.requestedBy} sx={{ minWidth: 170 }} onChange={(e) => setFilter('requestedBy', e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {(users.data?.data ?? []).map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.fullName}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={f.status} sx={{ minWidth: 140 }} onChange={(e) => setFilter('status', e.target.value)}>
          {['', 'draft', 'submitted', 'paid', 'rejected'].map((s) => <MenuItem key={s || 'all'} value={s}>{s ? s : 'All'}</MenuItem>)}
        </TextField>
        <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={f.from} onChange={(e) => setFilter('from', e.target.value)} />
        <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={f.to} onChange={(e) => setFilter('to', e.target.value)} />
      </Stack>
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Payments fontSize="small" /></IconButton></Tooltip>} />

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />}
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function CreateDialog({ onClose, onDone, onError, error }: {
  onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [sourceType, setSourceType] = useState<'work_order' | 'local_purchase'>('work_order');
  const [sourceId, setSourceId] = useState('');
  const [paymentType, setPaymentType] = useState('running');
  const [paymentMode, setPaymentMode] = useState('NEFT');
  const [amount, setAmount] = useState('');
  const [percent, setPercent] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [remarks, setRemarks] = useState('');

  const { data: wos } = useQuery({ queryKey: ['epc-work-orders', 'for-pay'], queryFn: () => epcWorkOrderApi.list({ limit: 200 }), enabled: sourceType === 'work_order' });
  const { data: pos } = useQuery({ queryKey: ['epc-pos', 'for-pay'], queryFn: () => epcPoApi.list({ limit: 200 }), enabled: sourceType === 'local_purchase' });
  const wo = (wos?.data ?? []).find((w) => String(w.id) === String(sourceId));
  const po = (pos?.data ?? []).find((p) => String(p.id) === String(sourceId));
  // Full client / project / site / vendor(or shop) details for the chosen source.
  const { data: details } = useQuery({ queryKey: ['epc-pay-source', sourceType, sourceId], queryFn: () => epcPaymentApi.sourceDetails(sourceType, Number(sourceId)), enabled: Boolean(sourceId) });
  const orderCode = details?.orderCode ?? (sourceType === 'work_order' ? wo?.workOrderCode : po?.poCode);
  const orderValue = Number(details?.orderValue ?? (sourceType === 'work_order' ? wo?.orderValue : po?.totalAmount) ?? 0);

  const pick = (v: string) => {
    setSourceId(v);
    const s = sourceType === 'work_order' ? (wos?.data ?? []).find((w) => String(w.id) === String(v)) : (pos?.data ?? []).find((p) => String(p.id) === String(v));
    if (s) { const base = Number(sourceType === 'work_order' ? (s as { orderValue: number }).orderValue : (s as { totalAmount: number }).totalAmount) || 0; setAmount(String(base)); setPercent('100'); }
  };

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  const onAmount = (v: string) => { setAmount(v); setPercent(orderValue > 0 && v !== '' ? String(round2((Number(v) / orderValue) * 100)) : ''); };
  const onPercent = (v: string) => { setPercent(v); setAmount(orderValue > 0 && v !== '' ? String(round2((orderValue * Number(v)) / 100)) : ''); };

  const mut = useMutation({
    mutationFn: () => {
      const payeeType: 'vendor' | 'technician' = sourceType === 'work_order' && wo?.technicianId && !wo?.vendorId ? 'technician' : 'vendor';
      const projectId = Number((sourceType === 'work_order' ? wo?.projectId : po?.projectId) ?? 0);
      return epcPaymentApi.create({
        projectId, sourceType, paymentType, paymentMode, amount: Number(amount),
        workOrderId: sourceType === 'work_order' ? Number(sourceId) : undefined,
        purchaseOrderId: sourceType === 'local_purchase' ? Number(sourceId) : undefined,
        payeeType,
        vendorId: payeeType === 'vendor' ? ((sourceType === 'work_order' ? wo?.vendorId : po?.vendorId) ?? undefined) : undefined,
        technicianId: payeeType === 'technician' ? (wo?.technicianId ?? undefined) : undefined,
        attachmentUrl: attachmentUrl || undefined, remarks: remarks || undefined,
      });
    },
    onSuccess: (p) => onDone(p.id),
    onError: (e) => onError(extractApiError(e)),
  });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Payment Request</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField select label="Against" size="small" fullWidth value={sourceType} onChange={(e) => { setSourceType(e.target.value as 'work_order' | 'local_purchase'); setSourceId(''); setAmount(''); }}>
              <MenuItem value="work_order">Work Order</MenuItem>
              <MenuItem value="local_purchase">Local Purchase Order</MenuItem>
            </TextField>
            <TextField select label={sourceType === 'work_order' ? 'Work Order *' : 'Local Purchase *'} size="small" fullWidth value={sourceId} onChange={(e) => pick(e.target.value)}>
              {sourceType === 'work_order'
                ? (wos?.data ?? []).map((w) => <MenuItem key={w.id} value={String(w.id)}>{w.workOrderCode} — {w.title}</MenuItem>)
                : (pos?.data ?? []).map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.poCode} — {p.supplierName ?? p.vendorName ?? 'Shop'}</MenuItem>)}
            </TextField>
          </Stack>

          {sourceId && details && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>{orderCode} · Order value {inr(orderValue)}</Typography>
              <Stack direction="row" spacing={3} rowGap={1.5} flexWrap="wrap" useFlexGap>
                {details.client && <DetailBlock title="Client" lines={[details.client.name, details.client.gstNumber && `GST: ${details.client.gstNumber}`, details.client.address, [details.client.contact, details.client.mobile, details.client.email].filter(Boolean).join(' · ')]} />}
                {details.project && <DetailBlock title="Project" lines={[`${details.project.name} (${details.project.code})`]} />}
                {details.site && <DetailBlock title="Site" lines={[`${details.site.name} (${details.site.code})`, details.site.address]} />}
                {details.vendor && <DetailBlock title="Vendor" lines={[details.vendor.name, details.vendor.gstNumber && `GST: ${details.vendor.gstNumber}`, details.vendor.panNumber && `PAN: ${details.vendor.panNumber}`, details.vendor.address, (details.vendor.bankName || details.vendor.bankAccount || details.vendor.ifsc) && `Bank: ${[details.vendor.bankName, details.vendor.bankAccount, details.vendor.ifsc].filter(Boolean).join(' / ')}`, [details.vendor.mobile, details.vendor.email].filter(Boolean).join(' · ')]} />}
                {!details.vendor && details.shop && <DetailBlock title="Shop / Supplier" lines={[details.shop.name, details.shop.gstNumber && `GST: ${details.shop.gstNumber}`, details.shop.address, [details.shop.contact, details.shop.mobile].filter(Boolean).join(' · ')]} />}
                {!details.vendor && !details.shop && details.technician && <DetailBlock title="Technician" lines={[details.technician.name, details.technician.mobile]} />}
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={2}>
            <TextField select label="Payment Type" size="small" fullWidth value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              <MenuItem value="advance">Advance</MenuItem><MenuItem value="running">Running</MenuItem><MenuItem value="final">Final</MenuItem>
            </TextField>
            <TextField select label="Payment Mode" size="small" fullWidth value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
              {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="% of order value" type="number" size="small" fullWidth value={percent} onChange={(e) => onPercent(e.target.value)} disabled={orderValue <= 0} helperText={orderValue > 0 ? `of ${inr(orderValue)}` : 'order value is 0'} />
            <TextField label="Requested Amount (₹) *" type="number" size="small" fullWidth value={amount} onChange={(e) => onAmount(e.target.value)} helperText="Amount ↔ % auto-calculate" />
          </Stack>
          <TextField label="Remarks / Justification" size="small" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          <FileUploadField label="Attachment (invoice / proof)" accept=".pdf,image/*,.xls,.xlsx" value={attachmentUrl} onChange={(url) => setAttachmentUrl(url ?? '')} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!sourceId || !amount || Number(amount) <= 0 || mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose, onChanged, onToast }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: p } = useQuery({ queryKey: ['epc-payment', id], queryFn: () => epcPaymentApi.get(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-payment', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  if (!p) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{p.paymentCode}
        {p.status === 'submitted' ? <Chip size="small" sx={{ ml: 1 }} label={p.stageLabel ?? 'In approval'} color="warning" /> : <Chip size="small" sx={{ ml: 1 }} label={p.status} color={statusColor(p.status)} />}
        <Typography variant="body2" color="text.secondary">{p.payeeName ?? p.payeeType} · {p.paymentType} · {inr(p.amount)}{p.status === 'paid' && p.paymentRef ? ` · ${p.paymentRef}` : ''}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {p.status === 'rejected' && p.remarks && <Alert severity="warning">Rejected: {p.remarks}</Alert>}
        <Stack spacing={0.5}>
          <Typography variant="body2">Project #{p.projectId}{p.workOrderId ? ` · Work Order #${p.workOrderId}` : ''}{p.purchaseOrderId ? ` · Local Purchase #${p.purchaseOrderId}` : ''}</Typography>
          <Typography variant="body2"><b>Payee:</b> {p.payeeName ?? p.payeeType}</Typography>
          <Typography variant="body2"><b>Payment:</b> {p.paymentType}{p.paymentMode ? ` · ${p.paymentMode}` : ''} · {inr(p.amount)}</Typography>
          {p.remarks && p.status !== 'rejected' && <Typography variant="body2"><b>Remarks:</b> {p.remarks}</Typography>}
          {p.attachmentUrl && <Typography variant="body2"><b>Attachment:</b> <a href={p.attachmentUrl} target="_blank" rel="noreferrer">View</a></Typography>}
        </Stack>
        {(p.stageHistory?.length ?? 0) > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">Approval trail</Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {(p.stageHistory ?? []).map((h, i) => {
                const hist = p.stageHistory ?? [];
                const tat = i > 0 ? new Date(h.at).getTime() - new Date(hist[i - 1].at).getTime() : 0;
                return (
                  <Typography key={i} variant="caption" color="text.secondary">
                    {h.stageLabel ?? h.stage} — {h.action}{h.byName ? ` by ${h.byName}` : ''} · {new Date(h.at).toLocaleString('en-IN')}{i > 0 ? ` · took ${fmtTat(tat)}` : ''}{h.remarks ? ` · ${h.remarks}` : ''}
                  </Typography>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {p.status === 'draft' && hasPermission('epc_payment.delete') && <Button color="error" onClick={() => act(() => epcPaymentApi.remove(id).then(() => onClose()), 'Payment deleted')}>Delete</Button>}
        {(p.status === 'submitted' || p.status === 'rejected') && hasPermission('epc_payment.update') && <Button color="warning" onClick={() => act(() => epcPaymentApi.revise(id), 'Sent back for revision')}>Revise</Button>}
        {p.status === 'draft' && hasPermission('epc_payment.submit') && <Button variant="contained" onClick={() => act(() => epcPaymentApi.submit(id), 'Submitted for approval')}>Submit</Button>}
        {p.status === 'submitted' && p.stage !== 'finance' && hasPermission('epc_payment.approve') && (
          <>
            <Button color="error" onClick={() => { const r = window.prompt('Rejection reason?'); if (r) act(() => epcPaymentApi.reject(id, r), 'Rejected'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => act(() => epcPaymentApi.approve(id), `Approved — moved to next stage`)}>Approve ({p.stageLabel})</Button>
          </>
        )}
        {p.status === 'submitted' && p.stage === 'finance' && hasPermission('epc_payment.pay') && (
          <>
            <Button color="error" onClick={() => { const r = window.prompt('Rejection reason?'); if (r) act(() => epcPaymentApi.reject(id, r), 'Rejected'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => { const ref = window.prompt('Payment reference (optional)?') ?? undefined; act(() => epcPaymentApi.pay(id, { paymentRef: ref }), 'Payment transferred'); }}>Approve & Transfer</Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
